import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('trebol_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('trebol_token');
      localStorage.removeItem('trebol_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', null, { params: data }),
  completeOnboarding: () => api.put('/auth/onboarding-complete'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Dashboard
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard'),
};

// Transactions
export const transactionsAPI = {
  getAll: (params) => api.get('/transactions', { params }),
  getOne: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Accounts
export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

// Reports
export const reportsAPI = {
  generate: (data) => api.post('/reports', data),
};

// Calculators
export const calculatorsAPI = {
  savings: (data) => api.post('/calculators/savings', null, { params: data }),
  expenseImpact: (data) => api.post('/calculators/expense-impact', null, { params: data }),
  investment: (data) => api.post('/calculators/investment', null, { params: data }),
};

// AI Assistant
export const assistantAPI = {
  getInsights: () => api.get('/assistant/insights'),
};

// Tips
export const tipsAPI = {
  get: () => api.get('/tips'),
};

// Mock Banking
export const bankingAPI = {
  getMockTransactions: () => api.get('/banking/mock-transactions'),
};

// Recurring Transactions
export const recurringAPI = {
  getAll: () => api.get('/recurring-transactions'),
  create: (data) => api.post('/recurring-transactions', data),
  update: (id, data) => api.put(`/recurring-transactions/${id}`, data),
  delete: (id) => api.delete(`/recurring-transactions/${id}`),
  execute: (id) => api.post(`/recurring-transactions/${id}/execute`),
};

// Budgets
export const budgetsAPI = {
  getAll: () => api.get('/budgets'),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  checkAlerts: () => api.post('/budgets/check-alerts'),
};

// Notifications
export const notificationsAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Savings Goals
export const savingsGoalsAPI = {
  getAll: () => api.get('/savings-goals'),
  create: (data) => api.post('/savings-goals', data),
  update: (id, data) => api.put(`/savings-goals/${id}`, data),
  delete: (id) => api.delete(`/savings-goals/${id}`),
  addFunds: (id, amount) => api.post(`/savings-goals/${id}/add-funds`, null, { params: { amount } }),
};

// Export Reports
export const exportAPI = {
  excel: (data) => api.post('/reports/export/excel', data, { responseType: 'blob' }),
  pdf: (data) => api.post('/reports/export/pdf', data, { responseType: 'blob' }),
};

// OCR Receipt Scanning
export const ocrAPI = {
  scanReceipt: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/scan-receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmReceipt: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return api.post('/scan-receipt/confirm', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Cron / Scheduler
export const cronAPI = {
  executeRecurring: () => api.post('/cron/execute-recurring'),
  getStatus: () => api.get('/cron/status'),
};

// Budget History
export const budgetHistoryAPI = {
  getHistory: (budgetId, months = 6) => api.get(`/budgets/${budgetId}/history`, { params: { months } }),
  getOverview: (months = 6) => api.get('/budgets/overview', { params: { months } }),
};

// 2FA
export const twoFactorAPI = {
  setup: () => api.post('/auth/2fa/setup'),
  verify: (code) => api.post('/auth/2fa/verify', { code }),
  disable: (code) => api.post('/auth/2fa/disable', { code }),
  getStatus: () => api.get('/auth/2fa/status'),
};

// Investments
export const investmentsAPI = {
  getAll: () => api.get('/investments'),
  create: (data) => api.post('/investments', data),
  update: (id, data) => api.put(`/investments/${id}`, data),
  delete: (id) => api.delete(`/investments/${id}`),
  getPortfolio: () => api.get('/investments/portfolio'),
  updateAllPrices: () => api.post('/investments/update-prices'),
  getLivePrice: (id) => api.get(`/investments/${id}/price`),
  searchMarket: (query) => api.get(`/market/search/${query}`),
};

// Account Mode (Modalidad de Cuenta)
export const accountModeAPI = {
  getMode: () => api.get('/account/mode'),
  updateMode: (data) => api.post('/account/mode', data),
  getMembers: () => api.get('/account/members'),
  inviteMember: (email) => api.post('/account/members/invite', { email }),
  removeMember: (userId) => api.delete(`/account/members/${userId}`),
};

// Legacy Family (kept for compatibility)
export const familyAPI = {
  create: (data) => api.post('/family', data),
  get: () => api.get('/family'),
  invite: (data) => api.post('/family/invite', data),
  getTransactions: (limit = 50) => api.get('/family/transactions', { params: { limit } }),
};

// Predictive Analysis
export const analysisAPI = {
  predictExpenses: (monthsAhead = 3) => api.get('/analysis/predict-expenses', { params: { months_ahead: monthsAhead } }),
  savingsProjection: (params) => api.get('/analysis/savings-projection', { params }),
  detectAnomalies: () => api.get('/analysis/anomalies'),
};

// Push Notifications
export const pushAPI = {
  subscribe: (data) => api.post('/push/subscribe', data),
  unsubscribe: () => api.delete('/push/unsubscribe'),
};

// Voice Commands
export const voiceAPI = {
  transcribe: (audioBase64, language = 'es') =>
    api.post('/voice/transcribe', { audio_base64: audioBase64, language }),
};

// Tax/Fiscal
export const taxAPI = {
  getSummary: (year) => api.get(`/tax/summary/${year}`),
  getDeductibleCategories: () => api.get('/tax/deductible-categories'),
};

export default api;
